/**
 * Browser client for the speech-to-speech (Realtime voice) endpoints.
 *
 * The backend only mints an ephemeral OpenAI session token + bridges tool calls;
 * the actual audio runs peer-to-peer over WebRTC (see VoiceCall.tsx).
 */

import { useAuthStore } from '../../lib/authStore.js';

const BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) || '/api/v1';

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { authorization: `Bearer ${token}` } : {};
}

export interface VoiceStatus {
  enabled: boolean;
  configured: boolean;
  available: boolean;
}

export interface VoiceSession {
  client_secret: { value: string; expires_at: number };
  model: string;
  voice: string;
  toolCount?: number;
}

/** Is voice available for this tenant (module on + native OpenAI key present)? */
export async function getVoiceStatus(): Promise<VoiceStatus> {
  const res = await fetch(`${BASE}/ai/realtime/status`, { headers: authHeaders(), credentials: 'include' });
  if (!res.ok) throw new Error(`voice_status_${res.status}`);
  return (await res.json()) as VoiceStatus;
}

/** Mint an ephemeral Realtime session (instructions + permission-filtered tools). */
export async function mintVoiceSession(opts: { lang?: 'en' | 'ar'; voice?: string } = {}): Promise<VoiceSession> {
  const res = await fetch(`${BASE}/ai/realtime/session`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify(opts),
    credentials: 'include',
  });
  const data = (await res.json().catch(() => null)) as (VoiceSession & { cause?: { code?: string }; code?: string }) | null;
  if (!res.ok || !data?.client_secret?.value) {
    const code = data?.cause?.code ?? data?.code;
    throw new Error(code === 'voice_not_configured' ? 'voice_not_configured' : `voice_session_${res.status}`);
  }
  return data;
}

/** Execute one backend (data) tool the model requested, with the user's permissions. */
export async function runVoiceTool(name: string, args: Record<string, unknown>, lang?: 'en' | 'ar'): Promise<{ result: unknown }> {
  const res = await fetch(`${BASE}/ai/realtime/tool`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ name, arguments: args, lang }),
    credentials: 'include',
  });
  if (!res.ok) {
    let detail = '';
    try { detail = await res.text(); } catch { /* ignore */ }
    throw new Error(`voice_tool_${res.status}${detail ? `: ${detail.slice(0, 160)}` : ''}`);
  }
  return (await res.json()) as { result: unknown };
}
